import React, { useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Button,
  Chip,
  Divider,
  Fade,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import ArrowDropDownCircleOutlinedIcon from '@mui/icons-material/ArrowDropDownCircleOutlined';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import type { TypographyProps } from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import {
  useDeleteUserTagMutation,
  useGetUserTagsQuery,
  userTagCanDelete,
  userTagCanEdit,
  type UserTag,
} from '@/store/usersApi';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import {
  setDefaultViewingTag,
  setSelectedViewingTag,
} from '@/store/viewingTagSlice';
import type { WARootState } from '@/store/store';
import { useLocalization } from '@/hooks/useLocalization';
import {
  buildViewingTagOptions,
  type ViewingTagOption,
} from '@/utils/accountInvestorViewingOptions';
import type { ViewingTagSelection } from '@/utils/viewingTagSelection';
import { headerOutlinedFieldStyles } from '@/constants/headerOutlinedFieldSx';
import { useShowTagSelectorDropdown } from '@/hooks/useShowTagSelectorDropdown';
import { useDefaultViewingTag } from '@/hooks/useDefaultViewingTag';
import TagUpsertModal from '@/components/common/TagUpsertModal';

export type TagOption = ViewingTagOption;

interface TagSelectorDropdownProps {
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  disabled?: boolean;
  /** Controls whether the autocomplete control is shown. */
  showSelector?: boolean;
  /** Optional inline title text in format "{title} for {selectedValue}". */
  inlineTitle?: string;
  /** Optional typography variant for inline title text. */
  inlineTitleVariant?: TypographyProps['variant'];
}

function selectionsEqual(
  left: ViewingTagSelection,
  right: ViewingTagSelection,
): boolean {
  if (left === null || right === null) {
    return left === right;
  }
  if (left.source === 'firm' && right.source === 'firm') {
    return left.tagId === right.tagId;
  }
  if (left.source === 'user' && right.source === 'user') {
    return left.tagId === right.tagId;
  }
  return false;
}

function optionMatchesSelection(
  option: TagOption,
  selection: ViewingTagSelection,
): boolean {
  return (
    option.selection !== undefined &&
    selectionsEqual(option.selection, selection)
  );
}

function optionKey(option: TagOption): string {
  if (option.kind === 'all' || option.kind === 'create') {
    return option.kind;
  }
  if (option.selection?.source === 'firm') {
    return `firm-${String(option.selection.tagId)}`;
  }
  if (option.selection?.source === 'user') {
    return `user-${String(option.selection.tagId)}`;
  }
  return `${option.kind}-${option.label}`;
}

/**
 * Global account-view selector for firm tags and investor-created personal tags.
 * The Redux value remains source-aware so personal ids are never mistaken for firm ids.
 */
const TagSelectorDropdown: React.FC<TagSelectorDropdownProps> = ({
  size = 'medium',
  sx,
  disabled = false,
  showSelector = true,
  inlineTitle,
  inlineTitleVariant,
}) => {
  const showForInstitution = useShowTagSelectorDropdown();
  const { t } = useLocalization();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [tagMenuAnchor, setTagMenuAnchor] = useState<HTMLElement | null>(null);
  const [tagActionsAnchor, setTagActionsAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [tagActionsTarget, setTagActionsTarget] = useState<UserTag>();
  const highlightedOptionRef = useRef<TagOption | null>(null);
  const [upsertOpen, setUpsertOpen] = useState(false);
  const [tagToEdit, setTagToEdit] = useState<UserTag>();
  const [tagToDelete, setTagToDelete] = useState<UserTag>();
  const [deleteError, setDeleteError] = useState(false);
  const tagMenuOpen = Boolean(tagMenuAnchor);

  const selectedTag = useSelector(
    (state: WARootState) => state.viewingTag.selectedViewingTag,
  );
  const defaultTag = useSelector(
    (state: WARootState) => state.viewingTag.defaultViewingTag,
  );

  const authLoaded = useAuthIsLoaded();
  const {
    data: userTags,
    isLoading: userTagsLoading,
    isError: userTagsError,
    refetch: refetchUserTags,
  } = useGetUserTagsQuery(undefined, {
    skip: !showForInstitution || !authLoaded,
  });
  const [deleteUserTag, { isLoading: isDeleting }] = useDeleteUserTagMutation();

  const { saveDefaultViewingTag } = useDefaultViewingTag();

  const options = useMemo(
    () =>
      buildViewingTagOptions({
        allAccountsLabel: t('viewingTag.allAccounts'),
        createTagLabel: t('viewingTag.createTag'),
        userTags,
      }),
    [userTags, t],
  );
  const existingTagNames = useMemo(
    () => (userTags ?? []).map((tag) => tag.name),
    [userTags],
  );

  const value = useMemo<TagOption | null>(() => {
    const matchingOption = options.find((option) =>
      optionMatchesSelection(option, selectedTag),
    );
    if (matchingOption) {
      return matchingOption;
    }
    if (selectedTag?.source === 'user') {
      return {
        kind: 'user',
        label: t('viewingTag.selectedPersonalTag'),
        selection: selectedTag,
        group: 'user',
      };
    }
    return options.find((option) => option.kind === 'all') ?? null;
  }, [options, selectedTag, t]);

  const hasSelectableTags = options.some(
    (option) => option.kind === 'firm' || option.kind === 'user',
  );
  const isUsingFirmDefault = defaultTag === null;
  const loading = userTagsLoading;
  const tagsLoadFailed = userTagsError;
  const showInlineTitle = Boolean(inlineTitle);
  const inlineOnly = showInlineTitle && !showSelector;
  const selectedLabel = value?.label ?? t('viewingTag.allAccounts');

  const handleOpenCreate = (): void => {
    setTagToEdit(undefined);
    setUpsertOpen(true);
    setTagMenuAnchor(null);
    setTagActionsAnchor(null);
    setTagActionsTarget(undefined);
  };

  const handleChange = (
    _: React.SyntheticEvent,
    newValue: TagOption | null,
  ): void => {
    if (newValue?.kind === 'create') {
      handleOpenCreate();
      return;
    }
    dispatch(setSelectedViewingTag(newValue?.selection ?? null));
  };

  const handleInlineTagPick = (option: TagOption): void => {
    if (option.kind === 'create') {
      handleOpenCreate();
      return;
    }
    dispatch(setSelectedViewingTag(option.selection ?? null));
    setTagMenuAnchor(null);
  };

  const handleSaveAsDefault = (event: React.MouseEvent): void => {
    event.stopPropagation();
    saveDefaultViewingTag(selectedTag);
    dispatch(setDefaultViewingTag(selectedTag));
  };

  const handleResetToFirmDefault = (event: React.MouseEvent): void => {
    event.stopPropagation();
    saveDefaultViewingTag(null);
    dispatch(setDefaultViewingTag(null));
    dispatch(setSelectedViewingTag(null));
  };

  const handleOpenEdit = (tag: UserTag): void => {
    setTagToEdit(tag);
    setUpsertOpen(true);
    setTagMenuAnchor(null);
    setTagActionsAnchor(null);
    setTagActionsTarget(undefined);
  };

  const handleOpenDelete = (tag: UserTag): void => {
    setDeleteError(false);
    setTagToDelete(tag);
    setTagMenuAnchor(null);
    setTagActionsAnchor(null);
    setTagActionsTarget(undefined);
  };

  const handleCloseTagActions = (): void => {
    setTagActionsAnchor(null);
    setTagActionsTarget(undefined);
  };

  const handleCloseDelete = (): void => {
    if (isDeleting) {
      return;
    }
    setDeleteError(false);
    setTagToDelete(undefined);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!tagToDelete || isDeleting) {
      return;
    }

    setDeleteError(false);
    const result = await deleteUserTag(tagToDelete.tagId);
    if ('error' in result || !result.data) {
      setDeleteError(true);
      return;
    }

    const deletedSelection: ViewingTagSelection = {
      source: 'user',
      tagId: tagToDelete.tagId,
    };
    if (selectionsEqual(selectedTag, deletedSelection)) {
      dispatch(setSelectedViewingTag(null));
    }
    if (selectionsEqual(defaultTag, deletedSelection)) {
      saveDefaultViewingTag(null);
      dispatch(setDefaultViewingTag(null));
    }
    setTagToDelete(undefined);
  };

  const handleRetryTags = (): void => {
    if (userTagsError) {
      void refetchUserTags();
    }
  };

  /** Resolves the editable/deletable personal tag behind a `user` option, if any. */
  const findPersonalTag = (option: TagOption): UserTag | undefined => {
    if (option.selection?.source !== 'user') {
      return undefined;
    }
    const tagId = option.selection.tagId;
    const tag = userTags?.find((candidate) => candidate.tagId === tagId);
    if (!tag || (!userTagCanEdit(tag) && !userTagCanDelete(tag))) {
      return undefined;
    }
    return tag;
  };

  const handleAutocompleteKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ): void => {
    const requestsTagActions =
      event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10');
    if (!requestsTagActions || highlightedOptionRef.current === null) {
      return;
    }

    const personalTag = findPersonalTag(highlightedOptionRef.current);
    if (!personalTag) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setTagActionsAnchor(event.currentTarget);
    setTagActionsTarget(personalTag);
  };

  const renderOptionExtras = (option: TagOption): React.ReactNode => {
    const isSelected = optionMatchesSelection(option, selectedTag);
    const isDefault = optionMatchesSelection(option, defaultTag);
    const personalTag = findPersonalTag(option);
    const actionsLabel = `${t('viewingTag.tagActions')}: ${option.label}`;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          ml: 'auto',
        }}
      >
        {isDefault && (
          <Chip
            size='small'
            label={t('default', 'Default')}
            sx={{ height: 20, pointerEvents: 'none' }}
          />
        )}
        {isSelected && !isDefault && hasSelectableTags && (
          <Fade in>
            <Box component='span'>
              <Tooltip title={t('saveAsDefault', 'Save as default')}>
                <IconButton
                  size='small'
                  aria-label={t('saveAsDefault', 'Save as default')}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={handleSaveAsDefault}
                  sx={{
                    opacity: 0.8,
                    transition: 'opacity 200ms ease',
                    '&:hover': { opacity: 1 },
                  }}
                >
                  <SaveIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Fade>
        )}
        {personalTag && (
          <Tooltip title={actionsLabel}>
            <IconButton
              size='small'
              aria-label={actionsLabel}
              aria-haspopup='menu'
              aria-expanded={
                tagActionsTarget?.tagId === personalTag.tagId
                  ? 'true'
                  : undefined
              }
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setTagActionsAnchor(event.currentTarget);
                setTagActionsTarget(personalTag);
              }}
              sx={{
                minHeight: 44,
                minWidth: 44,
                transition: 'background-color 200ms ease',
              }}
            >
              <MoreVertIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  const resetButton = (
    <Box>
      <Divider />
      <Box sx={{ px: 1, py: 0.5 }}>
        <Button
          size='small'
          startIcon={<RestoreIcon fontSize='small' />}
          disabled={isUsingFirmDefault || !hasSelectableTags}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onClick={handleResetToFirmDefault}
          aria-label={t('resetToFirmDefault', 'Reset to firm default')}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          {t('resetToFirmDefault', 'Reset to firm default')}
        </Button>
      </Box>
    </Box>
  );

  const renderInlineOption = (option: TagOption): React.ReactNode => {
    const createUnavailable =
      option.kind === 'create' && (userTagsLoading || userTagsError);
    return (
      <ListItem
        key={optionKey(option)}
        disablePadding
        secondaryAction={renderOptionExtras(option)}
        sx={{ pr: 0 }}
      >
        <ListItemButton
          role='option'
          aria-selected={optionMatchesSelection(option, selectedTag)}
          selected={optionMatchesSelection(option, selectedTag)}
          disabled={createUnavailable}
          sx={{ minHeight: 48, pr: option.kind === 'user' ? 11 : undefined }}
          onClick={() => {
            handleInlineTagPick(option);
          }}
        >
          {option.kind === 'create' && (
            <AddIcon
              fontSize='small'
              sx={{ mr: 1 }}
            />
          )}
          <ListItemText primary={option.label} />
        </ListItemButton>
      </ListItem>
    );
  };

  if (!showForInstitution) {
    return null;
  }

  const allOptions = options.filter((option) => option.group === '');
  const firmOptions = options.filter((option) => option.group === 'firm');
  const personalOptions = options.filter((option) => option.group === 'user');

  return (
    <Box sx={sx}>
      <Box
        aria-busy={loading || isDeleting}
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        {showInlineTitle && (
          <Typography
            component='div'
            variant={
              inlineTitleVariant ?? (size === 'small' ? 'subtitle1' : 'h6')
            }
            sx={{ whiteSpace: 'nowrap' }}
          >
            {inlineTitle}{' '}
            {inlineOnly ? (
              <>
                <Link
                  component='button'
                  type='button'
                  onClick={(event) => {
                    setTagMenuAnchor(event.currentTarget);
                  }}
                  underline='hover'
                  aria-expanded={tagMenuOpen ? 'true' : 'false'}
                  aria-haspopup='listbox'
                  sx={{
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    verticalAlign: 'baseline',
                    color: 'primary.main',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    transition: 'color 200ms ease',
                  }}
                >
                  {t('for', 'for')} {selectedLabel}
                  <ArrowDropDownCircleOutlinedIcon sx={{ fontSize: '1em' }} />
                </Link>
                <Popover
                  open={tagMenuOpen}
                  anchorEl={tagMenuAnchor}
                  onClose={() => {
                    setTagMenuAnchor(null);
                  }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  slotProps={{
                    paper: {
                      sx: {
                        minWidth: 280,
                        maxHeight: 400,
                        mt: 0.5,
                      },
                    },
                  }}
                >
                  {loading && options.length <= 2 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 2,
                      }}
                    >
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <>
                      <List
                        dense
                        disablePadding
                        role='listbox'
                        aria-label={t('viewingTag.viewingAccountsFor')}
                      >
                        {allOptions.map(renderInlineOption)}
                        {firmOptions.length > 0 && (
                          <ListSubheader component='div'>
                            {t('viewingTag.myFirm')}
                          </ListSubheader>
                        )}
                        {firmOptions.map(renderInlineOption)}
                        {personalOptions.length > 0 && (
                          <ListSubheader component='div'>
                            {t('viewingTag.myTags')}
                          </ListSubheader>
                        )}
                        {personalOptions.map(renderInlineOption)}
                      </List>
                      {resetButton}
                    </>
                  )}
                </Popover>
              </>
            ) : (
              <>
                {t('for', 'for')} {selectedLabel}
              </>
            )}
          </Typography>
        )}
        {showSelector && (
          <Autocomplete<TagOption>
            size={size}
            options={options}
            value={value}
            onChange={handleChange}
            onHighlightChange={(_event, option) => {
              highlightedOptionRef.current = option;
            }}
            onKeyDown={handleAutocompleteKeyDown}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(left, right) =>
              left.kind === right.kind &&
              left.selection !== undefined &&
              right.selection !== undefined &&
              selectionsEqual(left.selection, right.selection)
            }
            groupBy={(option) => option.group}
            renderGroup={(params) => (
              <li key={params.key}>
                {params.group !== '' && (
                  <ListSubheader component='div'>
                    {params.group === 'firm'
                      ? t('viewingTag.myFirm')
                      : t('viewingTag.myTags')}
                  </ListSubheader>
                )}
                <ul>{params.children}</ul>
              </li>
            )}
            getOptionDisabled={(option) =>
              option.kind === 'create' && (userTagsLoading || userTagsError)
            }
            loading={loading}
            disabled={disabled}
            renderOption={(props, option) =>
              React.createElement(
                'li',
                props,
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    gap: 1,
                  }}
                >
                  {option.kind === 'create' && <AddIcon fontSize='small' />}
                  <span style={{ flex: 1 }}>{option.label}</span>
                  {renderOptionExtras(option)}
                </Box>,
              )
            }
            slots={{
              paper: React.forwardRef(function AutocompletePaper(
                { children, ...paperProps },
                ref,
              ) {
                return (
                  <Box
                    ref={ref}
                    {...paperProps}
                  >
                    {children}
                    {resetButton}
                  </Box>
                );
              }),
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size={size}
                label={t('viewingTag.viewingAccountsFor')}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading && (
                          <CircularProgress
                            size={18}
                            color='inherit'
                          />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                  inputLabel: { shrink: true },
                }}
                sx={(currentTheme) => ({
                  ...headerOutlinedFieldStyles(currentTheme, size),
                  minWidth: 0,
                })}
              />
            )}
            sx={{
              minWidth: 300,
              [theme.breakpoints.only('xs')]: { minWidth: 120 },
            }}
            slotProps={{
              popper: {
                placement: 'bottom-start',
                sx: { zIndex: theme.zIndex.modal },
              },
            }}
          />
        )}
      </Box>

      {tagsLoadFailed && (
        <Alert
          severity='error'
          role='alert'
          action={
            <Button
              color='inherit'
              size='small'
              onClick={handleRetryTags}
            >
              {t('retry', 'Retry')}
            </Button>
          }
          sx={{ mt: 0.75, alignItems: 'center' }}
        >
          {t('viewingTag.tagsLoadFailed')}
        </Alert>
      )}

      <Menu
        anchorEl={tagActionsAnchor}
        open={Boolean(tagActionsAnchor) && tagActionsTarget !== undefined}
        onClose={handleCloseTagActions}
        slotProps={{
          list: {
            'aria-label': t('viewingTag.tagActions'),
          },
        }}
      >
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            if (tagActionsTarget) {
              handleOpenEdit(tagActionsTarget);
            }
          }}
          sx={{ minHeight: 44 }}
        >
          <EditOutlinedIcon
            fontSize='small'
            sx={{ mr: 1 }}
          />
          {t('edit', 'Edit')}
        </MenuItem>
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            if (tagActionsTarget) {
              handleOpenDelete(tagActionsTarget);
            }
          }}
          sx={{ minHeight: 44, color: 'error.main' }}
        >
          <DeleteOutlineIcon
            fontSize='small'
            sx={{ mr: 1 }}
          />
          {t('delete', 'Delete')}
        </MenuItem>
      </Menu>

      <TagUpsertModal
        open={upsertOpen}
        onClose={() => {
          setUpsertOpen(false);
          setTagToEdit(undefined);
        }}
        existingTagNames={existingTagNames}
        tag={tagToEdit}
      />

      <Dialog
        open={tagToDelete !== undefined}
        onClose={handleCloseDelete}
        aria-labelledby='delete-investor-tag-title'
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle id='delete-investor-tag-title'>
          {t('delete', 'Delete')}
        </DialogTitle>
        <DialogContent aria-busy={isDeleting}>
          <DialogContentText>{t('viewingTag.deleteConfirm')}</DialogContentText>
          {deleteError && (
            <Alert
              severity='error'
              role='alert'
              sx={{ mt: 2 }}
            >
              {t('viewingTag.deleteFailed')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDelete}
            disabled={isDeleting}
          >
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            color='error'
            variant='contained'
            onClick={() => {
              void handleConfirmDelete();
            }}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : undefined}
          >
            {deleteError ? t('retry', 'Retry') : t('delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TagSelectorDropdown;
